import React, { useState, useEffect } from 'react'; // eslint-disable-line
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { api } from './shared';

// Platforms — top-level filter dropdown
const PLATFORMS = ['All', 'LINE', 'Facebook', 'Website', 'Other'];
const PLATFORM_COLORS = { LINE: '#06C755', Facebook: '#1877F2', Website: '#E67E22', Other: '#888888' };

// Smart categorization — รวม source ดิบเป็นหมวดที่อ่านง่าย
// ลำดับสำคัญ: match จาก specific → general; แต่ละ category ระบุ platform ที่สังกัด
const CATEGORIES = [
  // LINE
  { key: 'LINE OA Hyp',              platform: 'LINE', color: '#06C755', match: (s) => /line_oa_big_c_th/i.test(s) },
  { key: 'LINE OA Mini',             platform: 'LINE', color: '#16A085', match: (s) => /line_oa_big_c_mini/i.test(s) },
  { key: 'LIFF (Game share link)',   platform: 'LINE', color: '#0E8C7B', match: (s) => /liff|access\.line\.me/i.test(s) },
  { key: 'Web Digital Connect',      platform: 'LINE', color: '#27AE60', match: (s) => /line_connect/i.test(s) },
  { key: 'LINE Other',               platform: 'LINE', color: '#82E0AA', match: (s, m) => /line/i.test(s) || /line/i.test(m) },
  // Facebook — รวมเป็นหมวดเดียว
  { key: 'Facebook',                 platform: 'Facebook', color: '#1877F2', match: (s) => /facebook|fb\.com|m\.facebook|^fb$/i.test(s) },
  // Website
  { key: 'BigC Corporate Web',       platform: 'Website', color: '#E67E22', match: (s) => /corp_big_c|bigc\.co/i.test(s) },
  { key: 'BigC Online Catalog',      platform: 'Website', color: '#F39C12', match: (s) => /bigc-online/i.test(s) },
  // Other — รวม Direct, SMS, Google, อื่นๆ ที่ไม่จัดเข้าหมวด LINE/Facebook/Website
  { key: 'Direct',                   platform: 'Other', color: '#888888', match: () => true },
];
const categoryColor = (key) => CATEGORIES.find(c => c.key === key)?.color || '#999';

// rewrite source name สำหรับแสดงในตาราง (ไม่กระทบ raw GA data)
const displaySource = (s) => {
  if (!s) return s;
  if (/^line_connect$/i.test(s)) return 'Web Digital Connect';
  if (/^access\.line\.me$/i.test(s)) return 'liff.line.me'; // รวม access.line.me แสดงเป็น liff
  if (/^\(not set\)$/i.test(s)) return 'other';
  return s;
};
const displayMedium = (m) => {
  if (!m) return m;
  if (/^\(not set\)$/i.test(m)) return 'other';
  return m;
};

const CAMPAIGN_START = '2026-04-27';
const daysSinceCampaign = () => Math.max(1, Math.ceil((Date.now() - new Date(CAMPAIGN_START).getTime()) / 86400000));

const RANGES = [
  { key: 7, label: '7 วันล่าสุด' },
  { key: 'campaign', label: 'ตั้งแต่เริ่ม Campaign', getDays: daysSinceCampaign },
];

const fmtDate = (yyyymmdd) => {
  if (!yyyymmdd || yyyymmdd.length !== 8) return yyyymmdd;
  return `${yyyymmdd.slice(0,4)}-${yyyymmdd.slice(4,6)}-${yyyymmdd.slice(6,8)}`;
};
const fmtDateShort = (yyyymmdd) => {
  if (!yyyymmdd || yyyymmdd.length !== 8) return yyyymmdd;
  return `${yyyymmdd.slice(4,6)}-${yyyymmdd.slice(6,8)}`;
};
const fmtDuration = (sec) => {
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};

const Card = ({ color, title, value, hint }) => (
  <div className="bts-admin__card" style={{ borderColor: color, textAlign:'center' }}>
    <div style={{ fontWeight:700, fontSize:13, color:'#555' }}>{title}</div>
    <div style={{ fontSize:24, fontWeight:800, color, marginTop:4 }}>{value}</div>
    {hint && <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{hint}</div>}
  </div>
);

const GaTab = ({ onError }) => {
  const [rangeKey, setRangeKey] = useState('campaign');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [platform, setPlatform] = useState('All');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const resolveDays = (k) => {
    if (typeof k === 'number') return k;
    const r = RANGES.find(x => x.key === k);
    return r?.getDays ? r.getDays() : 7;
  };
  const todayStr = new Date().toISOString().split('T')[0];
  const hasDateFilter = !!(startDate || endDate);

  const fetch = async () => {
    setLoading(true);
    try {
      let url;
      if (startDate && endDate) {
        url = `ga-report?startDate=${startDate}&endDate=${endDate}`;
      } else if (startDate) {
        url = `ga-report?date=${startDate}`;
      } else if (endDate) {
        url = `ga-report?date=${endDate}`;
      } else {
        url = `ga-report?days=${resolveDays(rangeKey)}`;
      }
      const r = await api(url);
      setReport(r.data?.data || null);
    } catch (e) {
      onError(e.response?.data?.error || e.message);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [rangeKey, startDate, endDate]);

  const pickRange = (k) => { setStartDate(''); setEndDate(''); setRangeKey(k); };
  const clearDates = () => { setStartDate(''); setEndDate(''); };

  const s = report?.summary;
  const chartData = (report?.timeSeries || []).map(r => ({
    date: fmtDateShort(r.date),
    fullDate: fmtDate(r.date),
    'ผู้ใช้': r.activeUsers,
    'Session': r.sessions,
    'PageViews': r.pageViews,
  }));

  return (
    <div className="bts-admin__section">
      <div className="bts-admin__topbar">
        <div className="bts-admin__page-title">📈 GA Traffic ({report?.pathPrefix || '/game/bts'})</div>
        <div className="bts-admin__actions">
          <button className="bts-admin__btn bts-admin__btn--dark" onClick={() => fetch()} disabled={loading}>
            {loading ? 'โหลด...' : 'รีเฟรช'}
          </button>
        </div>
      </div>

      {/* Filter bar — มุมซ้ายใต้ header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14, padding:'14px 18px', background:'#f8f9fa', borderRadius:10, border:'1px solid #e0e0e0', flexWrap:'wrap' }}>
        <span style={{ fontSize:16, fontWeight:700, color:'#333' }}>📅 ช่วงวันที่:</span>
        <input type="date" value={startDate} min={CAMPAIGN_START} max={endDate || todayStr}
          onChange={e => setStartDate(e.target.value)}
          style={{
            padding:'10px 14px', borderRadius:8,
            border: hasDateFilter ? '2px solid #459efc' : '1px solid #ccc',
            fontSize:16, fontWeight:600, fontFamily:'inherit',
            background:'#fff', cursor:'pointer', minWidth:170,
          }}
          disabled={loading} />
        <span style={{ fontSize:14, color:'#666' }}>ถึง</span>
        <input type="date" value={endDate} min={startDate || CAMPAIGN_START} max={todayStr}
          onChange={e => setEndDate(e.target.value)}
          style={{
            padding:'10px 14px', borderRadius:8,
            border: hasDateFilter ? '2px solid #459efc' : '1px solid #ccc',
            fontSize:16, fontWeight:600, fontFamily:'inherit',
            background:'#fff', cursor:'pointer', minWidth:170,
          }}
          disabled={loading} />
        {hasDateFilter && (
          <button onClick={clearDates}
            style={{ background:'#E24B4A', color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            ล้างตัวกรอง
          </button>
        )}
        <span style={{ borderLeft:'1px solid #ccc', height:28, margin:'0 6px' }} />
        {RANGES.map(r => (
          <button key={r.key}
            className={`bts-admin__btn ${!hasDateFilter && rangeKey === r.key ? 'bts-admin__btn--blue' : 'bts-admin__btn--dark'}`}
            onClick={() => pickRange(r.key)} disabled={loading}>
            {r.label}
          </button>
        ))}
      </div>

      <div style={{ background:'#fff8e6', border:'1px solid #FF8A00', borderRadius:8, padding:12, marginBottom:16, fontSize:13, color:'#7a4d00' }}>
        ⏱ ข้อมูลจาก Google Analytics 4 (Property <b>345887426</b>) อาจมี delay 24-48 ชม.
        {report?.mode === 'single'
          ? <> • วันที่ <b>{report.date}</b></>
          : hasDateFilter
            ? <> • ช่วง <b>{report?.startDate}</b> ถึง <b>{report?.endDate}</b> ({report?.days} วัน)</>
            : <> • {resolveDays(rangeKey)} วันล่าสุด</>}
      </div>

      {loading && <p>กำลังโหลด...</p>}

      {s && (<>
        {(() => {
          const newPct = s.activeUsers > 0 ? (s.newUsers / s.activeUsers * 100) : 0;
          const returningPct = Math.max(0, 100 - newPct);
          const sessionsPerUser = s.activeUsers > 0 ? (s.sessions / s.activeUsers) : 0;
          const pagesPerSession = s.sessions > 0 ? (s.pageViews / s.sessions) : 0;
          return (
            <div className="bts-admin__cards">
              <Card color="#9B59B6" title="👤 Active Users" value={s.activeUsers.toLocaleString()}
                hint={`ใหม่ ${newPct.toFixed(0)}% (${s.newUsers.toLocaleString()}) • กลับมา ${returningPct.toFixed(0)}%`} />
              <Card color="#459efc" title="🔁 Sessions" value={s.sessions.toLocaleString()}
                hint={`Sessions/User: ${sessionsPerUser.toFixed(2)}`} />
              <Card color="#10C851" title="📄 PageViews" value={s.pageViews.toLocaleString()}
                hint={`Pages/Session: ${pagesPerSession.toFixed(2)}`} />
              <Card color="#FF8A00" title="⏱ Avg Session" value={fmtDuration(s.avgSessionDurationSec)}
                hint={`เฉลี่ยต่อ session`} />
              <Card color="#E24B4A" title="↩️ Bounce Rate" value={`${(s.bounceRate * 100).toFixed(1)}%`}
                hint={s.bounceRate > 0.6 ? '🔴 สูง' : s.bounceRate > 0.4 ? '🟡 กลาง' : '🟢 ดี'} />
            </div>
          );
        })()}

        {/* Time-series chart */}
        <div style={{ background:'#fff', borderRadius:12, padding:'16px 8px', marginBottom:16, border:'1px solid #eee' }}>
          <div style={{ paddingLeft:8, marginBottom:8 }}>
            <p style={{ fontWeight:700, fontSize:14 }}>Traffic รายวัน</p>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background:'#fff', border:'1px solid #ddd', borderRadius:8, padding:'8px 12px', fontSize:12 }}>
                    <p style={{ fontWeight:700, marginBottom:4 }}>{d.fullDate}</p>
                    <p style={{ color:'#9B59B6' }}>ผู้ใช้: <b>{d['ผู้ใช้']}</b></p>
                    <p style={{ color:'#459efc' }}>Session: <b>{d['Session']}</b></p>
                    <p style={{ color:'#10C851' }}>PageViews: <b>{d['PageViews']}</b></p>
                  </div>
                );
              }} />
              <Legend />
              <Line type="monotone" dataKey="ผู้ใช้" stroke="#9B59B6" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Session" stroke="#459efc" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="PageViews" stroke="#10C851" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Platform tabs + Source breakdown: Pie chart + Detailed table */}
        {(() => {
          // 1) เก็บ raw sources พร้อม category + platform
          const rawWithCat = (report.sources || []).map(r => {
            const c = CATEGORIES.find(c => c.match((r.source || '').toLowerCase(), (r.medium || '').toLowerCase())) || CATEGORIES[CATEGORIES.length - 1];
            return { ...r, category: c.key, platformOf: c.platform };
          });
          // 2) Filter by selected platform
          const filtered = platform === 'All' ? rawWithCat : rawWithCat.filter(r => r.platformOf === platform);
          // 3) Pie data:
          //    - All → aggregate by platform (4 slices)
          //    - Platform → aggregate by sub-category
          let pieData;
          if (platform === 'All') {
            const byPlatform = {};
            rawWithCat.forEach(r => {
              if (!byPlatform[r.platformOf]) byPlatform[r.platformOf] = { name: r.platformOf, sessions: 0, activeUsers: 0, color: PLATFORM_COLORS[r.platformOf] || '#999' };
              byPlatform[r.platformOf].sessions += r.sessions;
              byPlatform[r.platformOf].activeUsers += r.activeUsers;
            });
            pieData = Object.values(byPlatform).sort((a,b) => b.sessions - a.sessions);
          } else {
            const byCat = {};
            filtered.forEach(r => {
              if (!byCat[r.category]) byCat[r.category] = { name: r.category, sessions: 0, activeUsers: 0, color: categoryColor(r.category) };
              byCat[r.category].sessions += r.sessions;
              byCat[r.category].activeUsers += r.activeUsers;
            });
            pieData = Object.values(byCat).sort((a,b) => b.sessions - a.sessions);
          }
          const totalSessions = pieData.reduce((s, x) => s + x.sessions, 0);

          return (
            <div style={{ background:'#fff', borderRadius:12, padding:'16px', marginBottom:16, border:'1px solid #eee' }}>
              {/* Platform tabs */}
              <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
                {PLATFORMS.map(p => (
                  <button key={p}
                    className={`bts-admin__btn bts-admin__btn--sm ${platform === p ? 'bts-admin__btn--blue' : 'bts-admin__btn--dark'}`}
                    onClick={() => setPlatform(p)}>
                    {p === 'All' ? '🌐 All' :
                     p === 'LINE' ? '🟢 LINE' :
                     p === 'Facebook' ? '🔵 Facebook' :
                     p === 'Website' ? '🟠 Website' : '⚪ Other'}
                  </button>
                ))}
              </div>
              <p style={{ fontWeight:700, fontSize:14, marginBottom:8 }}>Top Sources — {platform === 'All' ? 'จัดกลุ่มตาม Platform' : `รายละเอียด ${platform}`}</p>
              <div className="bts-ga__pie-row" style={{ display:'grid', gridTemplateColumns:'minmax(420px, 1.2fr) 1.5fr', gap:16, alignItems:'center' }}>
                <ResponsiveContainer width="100%" height={500}>
                  <PieChart margin={{ top: 30, right: 100, bottom: 30, left: 100 }}>
                    <Pie data={pieData} dataKey="sessions" nameKey="name" cx="50%" cy="50%" innerRadius={70} outerRadius={140}
                      labelLine={true}
                      label={(e) => `${e.name} ${totalSessions > 0 ? ((e.value/totalSessions)*100).toFixed(0) : 0}%`}>
                      {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v.toLocaleString()} sessions`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="bts-admin__table-wrap" style={{ marginBottom:0 }}>
                  <table className="bts-admin__table">
                    <thead><tr><th colSpan={4} style={{ textAlign:'center', background:'#1a1a2e' }}>Top Sources (ละเอียด) — {platform === 'All' ? 'ทั้งหมด' : platform}</th></tr>
                    <tr><th>หมวด / Source / Medium</th><th style={{ textAlign:'right' }}>Sessions</th><th style={{ textAlign:'right' }}>Users</th><th style={{ textAlign:'right' }}>%</th></tr></thead>
                    <tbody>{(() => {
                      if (filtered.length === 0) return <tr><td colSpan={4} style={{ textAlign:'center', color:'#888' }}>ไม่มีข้อมูล</td></tr>;
                      // group by category (sub-cat ถ้า platform เลือก / category ถ้า All)
                      const groups = {};
                      filtered.forEach(r => {
                        const k = r.category;
                        if (!groups[k]) groups[k] = { name: k, color: categoryColor(k), sessions: 0, activeUsers: 0, items: [] };
                        groups[k].sessions += r.sessions;
                        groups[k].activeUsers += r.activeUsers;
                        groups[k].items.push(r);
                      });
                      const sorted = Object.values(groups).sort((a,b) => b.sessions - a.sessions);
                      const rows = [];
                      sorted.forEach(g => {
                        rows.push(
                          <tr key={`g-${g.name}`} style={{ background:'#f0f0f0', fontWeight:700 }}>
                            <td><span style={{ display:'inline-block', width:10, height:10, background:g.color, borderRadius:2, marginRight:6 }} />{g.name}</td>
                            <td style={{ textAlign:'right' }}>{g.sessions.toLocaleString()}</td>
                            <td style={{ textAlign:'right' }}>{g.activeUsers.toLocaleString()}</td>
                            <td style={{ textAlign:'right' }}>{totalSessions > 0 ? ((g.sessions/totalSessions)*100).toFixed(1) : 0}%</td>
                          </tr>
                        );
                        // Web Digital Connect / LINE Other → ไม่ขยาย sub-rows
                        if (g.name === 'Web Digital Connect' || g.name === 'LINE Other') return;
                        // Aggregate raw items by (displaySource, displayMedium) — รวมตัวเลขเมื่อ alias เหมือนกัน
                        const aggItems = {};
                        g.items.forEach(r => {
                          const ds = displaySource(r.source);
                          const dm = displayMedium(r.medium);
                          const k = `${ds}|${dm}`;
                          if (!aggItems[k]) aggItems[k] = { source: ds, medium: dm, sessions: 0, activeUsers: 0 };
                          aggItems[k].sessions += r.sessions;
                          aggItems[k].activeUsers += r.activeUsers;
                        });
                        Object.values(aggItems)
                          .filter(r => r.sessions > 0)
                          .sort((a,b) => b.sessions - a.sessions)
                          .forEach((r, i) => {
                            rows.push(
                              <tr key={`g-${g.name}-${i}`} style={{ background:'#fff', fontSize:11 }}>
                                <td style={{ paddingLeft:28, color:'#555' }}>{r.source} <span style={{ color:'#aaa' }}>· {r.medium}</span></td>
                                <td style={{ textAlign:'right' }}>{r.sessions.toLocaleString()}</td>
                                <td style={{ textAlign:'right' }}>{r.activeUsers.toLocaleString()}</td>
                                <td></td>
                              </tr>
                            );
                          });
                      });
                      return rows;
                    })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}
      </>)}
      <div style={{ height: 100 }} />
    </div>
  );
};

export default GaTab;
